import { Archive, Download, Pencil, Play, Lock, Trash2 } from 'lucide-react'
import { download } from '../../api/client'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDeleteEvent, useEvent, useMe, useUpdateEvent } from '../../api/hooks'
import { Badge, Button, Modal, PageHeader, Spinner, Tabs } from '../../components/ui'
import { useToast } from '../../components/ui/toast'
import { EVENT_STATUS_LABEL, EVENT_TYPE_LABEL, fmtEventWhen } from '../../lib/format'
import { InvitationsTab } from '../invitations/InvitationsTab'
import { SendsTab } from '../invitations/SendsTab'
import { ParticipantsTab } from '../participants/ParticipantsTab'
import { CategoriesTab } from './CategoriesTab'
import { EventForm } from './EventForm'
import { OverviewTab } from './OverviewTab'

type TabKey = 'oversikt' | 'kategorier' | 'inbjudningar' | 'utskick' | 'deltagare'
const STATUS_TONE = { UTKAST: 'neutral', AKTIVT: 'success', STANGT: 'warning', ARKIVERAT: 'neutral' } as const

export function EventDetailPage() {
  const { id = '', tab } = useParams()
  const nav = useNavigate()
  const ev = useEvent(id)
  const me = useMe()
  const update = useUpdateEvent(id)
  const remove = useDeleteEvent()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isManager = me.data?.role !== 'RADGIVARE'
  const active: TabKey = (tab as TabKey) || 'oversikt'

  if (ev.isLoading) return <Spinner />
  if (ev.isError || !ev.data) return <div className="card p-6 text-sm text-ink-500">Eventet kunde inte hämtas.</div>
  const e = ev.data

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'oversikt', label: 'Översikt' },
    { key: 'kategorier', label: 'Kategorier', count: e.counts.categories },
    ...(isManager ? [{ key: 'inbjudningar' as TabKey, label: 'Inbjudningar' }] : []),
    { key: 'utskick', label: 'Utskick', count: e.counts.sends },
    { key: 'deltagare', label: 'Deltagare', count: e.counts.registered },
  ]

  const setStatus = (status: typeof e.status, msg: string) =>
    update.mutate({ status }, { onSuccess: () => toast.push('success', msg), onError: (err) => toast.push('error', err.message) })

  return (
    <>
      <PageHeader
        crumbs={<Link to="/" className="hover:text-navy-800">Event</Link>}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {e.name}
            <Badge tone={STATUS_TONE[e.status]} dot>{EVENT_STATUS_LABEL[e.status]}</Badge>
            {e.locked && <span title="Inbjudningar skickade – grunduppgifter låsta"><Lock className="size-4 text-ink-400" /></span>}
          </span>
        }
        subtitle={`${EVENT_TYPE_LABEL[e.event_type]} · ${fmtEventWhen(e.starts_at, e.ends_at)} · ${e.location}`}
        actions={
          <>
            <Button variant="ghost" icon={<Download className="size-4" />} onClick={() => download(`/api/events/${e.id}/export.xlsx`, `${e.name}.xlsx`)} title="Allt om eventet i en Excel-fil: översikt, per team, deltagare, kategorier, utskick">
              Excel
            </Button>
          {isManager && (
            <>
              {e.status === 'UTKAST' && (
                <Button variant="primary" icon={<Play className="size-4" />} loading={update.isPending} onClick={() => setStatus('AKTIVT', 'Eventet är nu aktivt – anmälningslänkarna fungerar')}>
                  Aktivera
                </Button>
              )}
              {e.status === 'AKTIVT' && <Button onClick={() => setStatus('STANGT', 'Anmälan stängd')}>Stäng anmälan</Button>}
              {e.status === 'STANGT' && <Button onClick={() => setStatus('AKTIVT', 'Anmälan öppnad igen')}>Öppna anmälan</Button>}
              <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>Redigera</Button>
              {e.status !== 'ARKIVERAT' && (
                <Button variant="ghost" icon={<Archive className="size-4" />} onClick={() => setStatus('ARKIVERAT', 'Eventet arkiverades')}>Arkivera</Button>
              )}
              {e.status === 'UTKAST' && !e.locked && (
                <Button variant="ghost" icon={<Trash2 className="size-4" />} className="text-danger-600 hover:bg-danger-50" onClick={() => setConfirmDelete(true)}>Radera</Button>
              )}
            </>
          )}
          </>
        }
      />

      <div className="mb-6"><Tabs tabs={tabs} value={active} onChange={(k) => nav(`/event/${id}/${k === 'oversikt' ? '' : k}`)} /></div>

      {active === 'oversikt' && <OverviewTab event={e} isManager={isManager} />}
      {active === 'kategorier' && <CategoriesTab event={e} isManager={isManager} />}
      {active === 'inbjudningar' && isManager && <InvitationsTab event={e} />}
      {active === 'utskick' && <SendsTab event={e} />}
      {active === 'deltagare' && <ParticipantsTab event={e} isManager={isManager} />}

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Radera utkastet?"
        footer={
          <>
            <Button onClick={() => setConfirmDelete(false)}>Avbryt</Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(e.id, { onSuccess: () => { toast.push('info', `"${e.name}" raderades`); nav('/') }, onError: (err) => toast.push('error', err.message) })}>Radera</Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">Utkastet <strong>{e.name}</strong> och dess kategorier och inbjudningsutkast tas bort permanent. Event som har skickats kan bara arkiveras.</p>
      </Modal>

      <Modal open={editing} onClose={() => setEditing(false)} title="Redigera event" wide>
        <EventForm
          initial={e}
          submitLabel="Spara"
          pending={update.isPending}
          error={update.error}
          onCancel={() => setEditing(false)}
          onSubmit={(data) => update.mutate(data, { onSuccess: () => { setEditing(false); toast.push('success', 'Eventet uppdaterades') } })}
        />
      </Modal>
    </>
  )
}
